import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  IntegrationPermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type {
  CatalogProperty,
  PropertyPortalPublication,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { TokenPagination } from 'src/modules/shared/components/molecules/paginations/token-pagination';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiCaretDown, PiCaretUp } from 'react-icons/pi';
import type { PropertyPublicationsSearch } from '../../schemas/property-publications-search.schema';
import type { PropertyPublicationPortalFilter } from '../../schemas/property-publications-search.schema';
import { PropertyPublicationsFiltersBar } from './property-publications-filters-bar';
import { PropertyPublicationsTable } from './property-publications-table';

interface PropertyPublicationsContentProps {
  loadingIntegrations: boolean;
  error: unknown;
  handleBack: () => void;
  refetch: () => Promise<unknown>;
  publications: PropertyPortalPublication[];
  loading: boolean;
  propertyById: Map<number, CatalogProperty | null>;
  /** Property IDs whose existing detail requests are pending. */
  loadingPropertyIds?: ReadonlySet<number>;
  handleOpenProperty: (propertyId: number) => void;
  isMeliConnected: boolean;
  isMeliSyncMode: boolean;
  onManagePublication: (publicationId: number) => void;
  pageSize: number;
  totalCount?: number;
  handlePageSizeChange: (size: number) => void;
  handleNextPage: () => void;
  hasNextPage: boolean;
  publicationStatus: PropertyPublicationsSearch['publicationStatus'];
  publicationPortal: PropertyPublicationPortalFilter;
  onPublicationStatusChange: (
    value: PropertyPublicationsSearch['publicationStatus']
  ) => void;
  onPublicationPortalChange: (value: PropertyPublicationPortalFilter) => void;
  isToolbarExpanded: boolean;
  onToolbarExpandedChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PropertyPublicationsContent: React.FC<
  PropertyPublicationsContentProps
> = ({
  loadingIntegrations,
  error,
  handleBack,
  refetch,
  publications,
  loading,
  propertyById,
  loadingPropertyIds,
  handleOpenProperty,
  isMeliConnected,
  isMeliSyncMode,
  onManagePublication,
  pageSize,
  totalCount,
  handlePageSizeChange,
  handleNextPage,
  hasNextPage,
  publicationStatus,
  publicationPortal,
  onPublicationStatusChange,
  onPublicationPortalChange,
  isToolbarExpanded,
  onToolbarExpandedChange,
}) => {
  const { t } = useI18n();
  const { hasPermission: canReadAll, loading: loadingPermissions } =
    useHasPermissions(PropertyPermission.Read);
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const { hasPermission: canReadPublications } = useHasPermissions(
    IntegrationPermission.Read
  );
  const canReadProperties = canReadAll || canReadScoped;

  if (loadingPermissions || loadingIntegrations) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text size="small" className="text-slate-500">
          {t('settings.credentials.loading')}
        </Text>
      </div>
    );
  }

  if (!canReadProperties) {
    return (
      <div className="flex h-full items-center justify-center">
        <ErrorState
          title={t('catalog.properties.permissions.readRequiredTitle')}
          description={t(
            'catalog.properties.permissions.readRequiredDescription'
          )}
          onAction={handleBack}
          actionContent={t('catalog.properties.editor.backToList')}
        />
      </div>
    );
  }

  if (!canReadPublications) {
    return (
      <div className="flex h-full items-center justify-center">
        <ErrorState
          title={t(
            'catalog.properties.publications.permissions.readRequiredTitle'
          )}
          description={t(
            'catalog.properties.publications.permissions.readRequiredDescription'
          )}
          onAction={handleBack}
          actionContent={t('catalog.properties.editor.backToList')}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <ErrorState
          title={t('catalog.properties.publications.error.title')}
          description={t('catalog.properties.publications.error.description')}
          onAction={() => void refetch()}
          actionContent={t('catalog.properties.error.retry')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-1 flex-col overflow-y-auto">
        <AnimatePresence initial={false}>
          {isToolbarExpanded ? (
            <motion.div
              key="publications-toolbar"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <PropertyPublicationsFiltersBar
                publicationStatus={publicationStatus}
                publicationPortal={publicationPortal}
                onPublicationStatusChange={onPublicationStatusChange}
                onPublicationPortalChange={onPublicationPortalChange}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className="sticky top-0 z-20 border-b border-slate-100 bg-white px-4 py-1">
          <TokenPagination
            className="bg-white"
            pageSize={pageSize}
            currentCount={publications.length}
            totalCount={totalCount}
            onPageSizeChange={handlePageSizeChange}
            onNextPage={handleNextPage}
            hasNextPage={hasNextPage}
            isLoading={loading}
            endAddon={
              <Tooltip
                content={
                  isToolbarExpanded
                    ? t('catalog.properties.toolbar.hideToolbar')
                    : t('catalog.properties.toolbar.showToolbar')
                }
                size="sm"
              >
                <Button
                  type="button"
                  variant="primary"
                  appearance="soft"
                  size="md"
                  isIconOnly
                  aria-label={
                    isToolbarExpanded
                      ? t('catalog.properties.toolbar.hideToolbar')
                      : t('catalog.properties.toolbar.showToolbar')
                  }
                  onClick={() =>
                    onToolbarExpandedChange((currentValue) => !currentValue)
                  }
                >
                  {isToolbarExpanded ? (
                    <PiCaretUp className="h-4 w-4" />
                  ) : (
                    <PiCaretDown className="h-4 w-4" />
                  )}
                </Button>
              </Tooltip>
            }
          />
        </div>

        <div className="px-4 pb-8 pt-4">
          <PropertyPublicationsTable
            publications={publications}
            loading={loading}
            propertyById={propertyById}
            loadingPropertyIds={loadingPropertyIds}
            isMeliConnected={isMeliConnected}
            isMeliSyncMode={isMeliSyncMode}
            onOpenProperty={handleOpenProperty}
            onManagePublication={onManagePublication}
          />
        </div>
      </div>
    </div>
  );
};
